import React, { useEffect, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { toast } from 'react-toastify';

import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Spinner } from '@/components/ui/spinner';
import config from 'constants/apiConfig';
import useIsOrgAdmin from 'hooks/useIsOrgAdmin';
import { useListExternalLayersQuery } from 'hooks/externalLayer/useListExternalLayersQuery';
import { externalLayersKeys } from 'hooks/externalLayer/queryKeys';
import { useListInternalLayersQuery } from 'hooks/internalLayer/useListInternalLayersQuery';
import { internalLayersKeys } from 'hooks/internalLayer/queryKeys';
import { parseError } from 'hooks/shared/error';
import { selectGroupGuid, useAppSelector } from 'slices/commonSelectors';

type LayerKind = 'internal' | 'external';

type HubLayerRow = {
  kind: LayerKind;
  id: string;
  name: string;
  defaultVisible: boolean;
};

function layerKey(layer: Pick<HubLayerRow, 'kind' | 'id'>) {
  return `${layer.kind}:${layer.id}`;
}

const OrgDefaultMapLayers: React.FC = () => {
  const groupGuid = useAppSelector(selectGroupGuid);
  const isOrgAdmin = useIsOrgAdmin();
  const queryClient = useQueryClient();
  const listEnabled = Boolean(groupGuid);

  const internalQuery = useListInternalLayersQuery({
    filter: { groupGuid: groupGuid ?? '' },
    perPage: 200,
    include: ['incident'],
  });
  const externalQuery = useListExternalLayersQuery(
    {
      filter: { groupGuid: groupGuid ?? '' },
      perPage: 200,
      include: ['incident'],
    },
    { enabled: listEnabled }
  );

  const layers = useMemo(() => {
    const internalRows: HubLayerRow[] = (internalQuery.data?.data ?? [])
      .filter((layer) => !layer.incidentId && !layer.deletedAt)
      .map((layer) => ({
        kind: 'internal' as const,
        id: layer.id,
        name: layer.name,
        defaultVisible: Boolean(layer.defaultVisible),
      }));
    const externalRows: HubLayerRow[] = (externalQuery.data?.data ?? [])
      .filter((layer) => !layer.incidentId)
      .map((layer) => ({
        kind: 'external' as const,
        id: layer.id,
        name: layer.name,
        defaultVisible: Boolean(layer.defaultVisible),
      }));
    return [...internalRows, ...externalRows].sort((a, b) =>
      a.name.localeCompare(b.name)
    );
  }, [externalQuery.data?.data, internalQuery.data?.data]);

  const [checked, setChecked] = useState<Record<string, boolean>>({});
  const [seeded, setSeeded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [query, setQuery] = useState('');

  useEffect(() => {
    if (seeded || internalQuery.isLoading || externalQuery.isLoading) return;
    if (!internalQuery.data && !externalQuery.data) return;
    const next: Record<string, boolean> = {};
    for (const layer of layers) {
      next[layerKey(layer)] = layer.defaultVisible;
    }
    setChecked(next);
    setSeeded(true);
  }, [
    externalQuery.data,
    externalQuery.isLoading,
    internalQuery.data,
    internalQuery.isLoading,
    layers,
    seeded,
  ]);

  const changed = layers.filter(
    (layer) => checked[layerKey(layer)] !== layer.defaultVisible
  );

  const save = async () => {
    setSaving(true);
    try {
      await Promise.all(
        changed.map((layer) => {
          const next = checked[layerKey(layer)];
          const path =
            layer.kind === 'internal'
              ? `${config.apiGateway.internal_layers}/${layer.id}`
              : `${config.apiGateway.external_layers}/${layer.id}`;
          return axios.patch(path, { defaultVisible: next });
        })
      );
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: internalLayersKeys.all() }),
        queryClient.invalidateQueries({ queryKey: externalLayersKeys.all() }),
      ]);
      toast.success('Default map layers saved.');
    } catch (error) {
      toast.error(parseError(error));
    } finally {
      setSaving(false);
    }
  };

  const isLoading = internalQuery.isLoading || externalQuery.isLoading;
  const needle = query.trim().toLowerCase();
  const visibleLayers = needle
    ? layers.filter((layer) => layer.name.toLowerCase().includes(needle))
    : layers;
  const internalLayers = visibleLayers.filter(
    (layer) => layer.kind === 'internal'
  );
  const externalLayers = visibleLayers.filter(
    (layer) => layer.kind === 'external'
  );

  return (
    <div className="dark tw:max-w-xl tw:space-y-4 tw:text-foreground">
      <div>
        <h2 className="tw:m-0 tw:text-lg tw:font-semibold">
          Default map layers
        </h2>
        <p className="tw:mt-1 tw:mb-0 tw:text-sm tw:text-muted-foreground">
          Layers checked here start visible when a member opens the map. Layers
          for an open incident still start on.
        </p>
      </div>
      {isLoading ? (
        <Spinner className="tw:size-6" />
      ) : layers.length === 0 ? (
        <p className="tw:mb-0 tw:text-sm tw:text-muted-foreground">
          This organization has no hub map layers yet.
        </p>
      ) : (
        <>
          <Input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search layers"
            aria-label="Search layers"
          />
          {visibleLayers.length === 0 ? (
            <p className="tw:mb-0 tw:text-sm tw:text-muted-foreground">
              No layers match that search.
            </p>
          ) : (
            <>
              <LayerGroup
                title="Pratus layers"
                layers={internalLayers}
                checked={checked}
                disabled={!isOrgAdmin || saving}
                onToggle={(key, value) =>
                  setChecked((prev) => ({ ...prev, [key]: value }))
                }
              />
              <LayerGroup
                title="ArcGIS layers"
                layers={externalLayers}
                checked={checked}
                disabled={!isOrgAdmin || saving}
                onToggle={(key, value) =>
                  setChecked((prev) => ({ ...prev, [key]: value }))
                }
              />
            </>
          )}
          {isOrgAdmin ? (
            <Button
              type="button"
              disabled={saving || changed.length === 0}
              onClick={() => {
                void save();
              }}
            >
              {saving ? 'Saving…' : 'Save'}
            </Button>
          ) : (
            <p className="tw:mb-0 tw:text-sm tw:text-muted-foreground">
              Only an organization admin can change these.
            </p>
          )}
        </>
      )}
    </div>
  );
};

function LayerGroup({
  title,
  layers,
  checked,
  disabled,
  onToggle,
}: {
  title: string;
  layers: HubLayerRow[];
  checked: Record<string, boolean>;
  disabled: boolean;
  onToggle: (key: string, value: boolean) => void;
}) {
  if (layers.length === 0) return null;
  return (
    <section className="tw:space-y-2">
      <h3 className="tw:m-0 tw:text-sm tw:font-semibold">{title}</h3>
      <ul className="tw:m-0 tw:list-none tw:space-y-2 tw:pl-0">
        {layers.map((layer) => {
          const key = layerKey(layer);
          return (
            <li key={key} className="tw:flex tw:items-center tw:gap-2">
              <Checkbox
                checked={Boolean(checked[key])}
                disabled={disabled}
                aria-label={`Show ${layer.name} on login`}
                onCheckedChange={(value) => onToggle(key, value === true)}
              />
              <span className="tw:text-sm">{layer.name}</span>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

export default OrgDefaultMapLayers;
